import React, { useState, useEffect } from 'react';
import { Eye, Upload, Clock, FileText, AlertCircle, Camera } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import secureStorage from '../utils/secureStorage';
import { API } from '../App';
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';

const RetinalScan = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);

  useEffect(() => {
    fetchScanHistory();
    
    // Cleanup camera stream on unmount
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const fetchScanHistory = async () => {
    try {
      const token = secureStorage.getToken();
      const response = await axios.get(`${API}/retinal-scan/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setScanHistory(response.data.scans);
      }
    } catch (error) {
      console.error('Failed to fetch scan history:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      setAnalysis(null);
      setSelectedScan(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an image first');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = secureStorage.getToken();
      const response = await axios.post(`${API}/retinal-scan/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setAnalysis(response.data.analysis);
        toast.success('Retinal scan analyzed successfully!');
        fetchScanHistory();
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze retinal scan');
    } finally {
      setLoading(false);
    }
  };

  const viewScanDetails = async (filename) => {
    try {
      const token = secureStorage.getToken();
      const response = await axios.get(`${API}/retinal-scan/scan/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSelectedScan(response.data.scan);
        setPreviewUrl(`data:${response.data.scan.content_type};base64,${response.data.scan.image_base64}`);
        setAnalysis(response.data.scan.analysis);
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Failed to fetch scan details:', error);
      toast.error('Failed to load scan details');
    }
  };

  const handleNewScan = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setAnalysis(null);
    setSelectedScan(null);
    setShowCamera(false);
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
      
      toast.success('Camera started');
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera. Please grant permission.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `retinal_scan_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedFile(file);
        setPreviewUrl(canvas.toDataURL('image/jpeg'));
        setShowCamera(false);
        
        // Stop camera stream
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
          setStream(null);
        }
        
        toast.success('Photo captured');
      }
    }, 'image/jpeg', 0.95);
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-center" richColors />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-xl">
              <Eye className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {t('retinalScan.title')}
              </h1>
              <p className="text-gray-600 text-base mt-1">
                {t('retinalScan.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Upload & Preview */}
          <div className="space-y-6">
            {/* Upload Card */}
            <Card className="p-6 shadow-lg">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Camera size={24} className="text-blue-500" />
                {t('retinalScan.captureOrUpload')}
              </h2>

              {showCamera ? (
                <div className="space-y-4">
                  <div className="relative bg-black rounded-lg overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full h-auto"
                    />
                    <canvas ref={canvasRef} className="hidden" />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={capturePhoto}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                    >
                      <Camera className="mr-2" size={20} />
                      {t('retinalScan.capturePhoto')}
                    </Button>
                    <Button
                      onClick={stopCamera}
                      variant="outline"
                      className="flex-1"
                    >
                      {t('retinalScan.cancel')}
                    </Button>
                  </div>
                </div>
              ) : !previewUrl ? (
                <div className="space-y-4">
                  {/* Camera Option */}
                  <Button
                    onClick={startCamera}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 py-6 text-lg"
                  >
                    <Camera className="mr-2" size={24} />
                    {t('retinalScan.openCamera')}
                  </Button>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">{t('voiceBiomarker.or')}</span>
                    </div>
                  </div>
                  
                  {/* Upload Option */}
                  <label className="block">
                    <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all">
                      <Upload size={40} className="mx-auto mb-3 text-blue-500" />
                      <p className="text-gray-700 font-medium mb-2">{t('retinalScan.uploadFromFiles')}</p>
                      <p className="text-sm text-gray-500">{t('retinalScan.supports')}</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFileSelect}
                    />
                  </label>
                </div>
              ) : (
                <div>
                  <div className="relative mb-4">
                    <img
                      src={previewUrl}
                      alt="Retinal scan preview"
                      className="w-full h-auto rounded-lg border-2 border-gray-200"
                    />
                  </div>
                  <div className="flex gap-3">
                    {!selectedScan && (
                      <Button
                        onClick={handleUpload}
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                      >
                        {loading ? t('retinalScan.analyzing') : t('retinalScan.analyzeScan')}
                      </Button>
                    )}
                    <Button
                      onClick={handleNewScan}
                      variant="outline"
                      className="flex-1"
                    >
                      {t('retinalScan.newScan')}
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Analysis Results */}
            {analysis && (
              <Card className="p-6 shadow-lg">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FileText size={24} className="text-purple-500" />
                  {t('retinalScan.analysisResults')}
                </h2>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
                  <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                      {analysis}
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                    <div className="flex items-start gap-2">
                      <AlertCircle size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-yellow-800">
                        <strong>{t('common.warning')}:</strong> {t('retinalScan.disclaimer')}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Scan History */}
          <div>
            <Card className="p-6 shadow-lg">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Clock size={24} className="text-green-500" />
                {t('retinalScan.scanHistory')}
              </h2>

              {scanHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Eye size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">{t('retinalScan.noPreviousScans')}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
                  {scanHistory.map((scan, index) => (
                    <div
                      key={`item-${index}`}
                      onClick={() => viewScanDetails(scan.filename)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedScan?.filename === scan.filename
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-800">{scan.filename}</h3>
                        <Eye size={20} className="text-blue-500" />
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock size={14} />
                        <span>{new Date(scan.created_at).toLocaleString()}</span>
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        Size: {(scan.file_size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetinalScan;
